import { useCallback, useRef } from 'react';
import { CACHE_TIMES } from '@/config/cache';

type RefetchFunction = () => void;
const PREFETCH_THROTTLE_TIME = CACHE_TIMES.SHORT;

/**
 * Custom hook to trigger a prefetch function on hover with throttling.
 * @param refetch - The function to call to trigger the data prefetch from React Query.
 * @param enabled - A condition that must be true for prefetching to occur, defaults to true.
 * @param throttleMs - The minimum time (in ms) between allowed prefetch calls, defaults to PREFETCH_THROTTLE_TIME.
 * @returns A callback function (`handlePrefetch`) to be attached to an `onMouseEnter` event.
 */
const usePrefetchOnHover = (
  refetch: RefetchFunction,
  enabled = true,
  throttleMs = PREFETCH_THROTTLE_TIME
) => {
  const lastPrefetchTime = useRef<number>(0);

  const handlePrefetch = useCallback(() => {
    const now = Date.now();
    const timeSinceLastPrefetch = now - lastPrefetchTime.current;

    if (enabled && (timeSinceLastPrefetch >= throttleMs || lastPrefetchTime.current === 0)) {
      refetch();
      lastPrefetchTime.current = now;
    }
  }, [enabled, refetch, throttleMs]);

  return handlePrefetch;
};

export default usePrefetchOnHover;
